import { Outlet, Link, createRootRoute, useRouterState } from "@tanstack/react-router";
import { Toaster } from "sonner";
import { Navbar } from "@/components/Navbar";
import { ColdStartBanner } from "@/components/ColdStartBanner";
import { AuthProvider } from "@/lib/auth/AuthContext";
import { useAccountProfileSync } from "@/lib/auth/useAccountProfileSync";
import { useEffect, useState } from "react";
import { wakeServer, onColdStart } from "@/lib/api";
import { RheaLoadingProvider } from "@/lib/ui/RheaLoadingProvider";

// Sprint: Auth Foundation V1 — mounted as a child of <AuthProvider> (never
// inside RootComponent directly, which renders AuthProvider itself and so
// is never one of ITS OWN context consumers). Renders nothing; only runs
// the best-effort logged-in preference sync side effect for the whole app,
// regardless of which route is currently showing.
function AccountProfileSyncMount() {
  useAccountProfileSync();
  return null;
}

function NotFoundComponent() {
  return (
    <div className="flex min-h-screen items-center justify-center px-4">
      <div className="max-w-md text-center">
        <h1 className="text-7xl font-bold text-foreground">404</h1>
        <h2 className="mt-4 text-xl font-semibold text-foreground">Página no encontrada</h2>
        <p className="mt-2 text-sm text-muted-foreground">La página que buscas no existe.</p>
        <div className="mt-6">
          <Link to="/" className="btn-gold inline-block">Volver al inicio</Link>
        </div>
      </div>
    </div>
  );
}

export const Route = createRootRoute({
  component: RootComponent,
  notFoundComponent: NotFoundComponent,
});

function RootComponent() {
  const [coldStart, setColdStart] = useState(false);
  const pathname = useRouterState({ select: s => s.location.pathname });
  const isHome = pathname === "/" || pathname.startsWith("/coming-soon") || pathname === "/experiences";

  useEffect(() => {
    wakeServer();
    return onColdStart(setColdStart);
  }, []);

  return (
    <AuthProvider>
      <AccountProfileSyncMount />
      <RheaLoadingProvider>
        {!isHome && <Navbar />}
        <ColdStartBanner visible={coldStart} />
        <Toaster position="top-right" richColors />
        <main className={isHome ? undefined : "pt-16"}>
          <Outlet />
        </main>
      </RheaLoadingProvider>
    </AuthProvider>
  );
}
